import type { CSSProperties } from 'react';
import { useEffect, useState } from 'react';
import { Alert, Button, Col, DatePicker, Form, Input, Row, Select, Space } from 'antd';
import type { FormInstance } from 'antd';
import dayjs from 'dayjs';
import { CheckCircleOutlined, LoadingOutlined } from '@ant-design/icons';
import { Permission, RESULTADO_VISITA, type ClienteRef } from '@aid/shared';
import { useHasPermission } from '../../../auth/useHasPermission';
import { useT } from '../../../locales';
import { ClienteSelect } from '../../../components/ClienteSelect';
import { DATE_FORMAT } from '../../../lib/date';

const itemStyle: CSSProperties = { marginBottom: 4 };

type GeoEstado = 'inactivo' | 'solicitando' | 'capturada' | 'denegada' | 'noSoportada';

interface Ubicacion {
  lat: number;
  lng: number;
  accuracy: number | null;
}

function solicitarUbicacion(
  setGeoEstado: (estado: GeoEstado) => void,
  setUbicacion: (ubicacion: Ubicacion) => void,
) {
  if (!navigator.geolocation) {
    setGeoEstado('noSoportada');
    return;
  }
  setGeoEstado('solicitando');
  navigator.geolocation.getCurrentPosition(
    (position) => {
      setUbicacion({
        lat: position.coords.latitude,
        lng: position.coords.longitude,
        accuracy: Number.isFinite(position.coords.accuracy) ? position.coords.accuracy : null,
      });
      setGeoEstado('capturada');
    },
    () => setGeoEstado('denegada'),
    { enableHighAccuracy: true, timeout: 10000, maximumAge: 30000 },
  );
}

interface VisitaFormProps {
  onSubmit: (values: Record<string, unknown>) => void;
  loading?: boolean;
  form?: FormInstance;
  hideSubmit?: boolean;
  /** Valores iniciales aplicados al montar (p. ej. cliente preseleccionado). */
  initialValues?: Record<string, unknown> | null;
  /** Compact client data from the cobrador worklist; avoids an admin lookup. */
  preselectedCliente?: ClienteRef;
  /** Notifies an enclosing modal when a field visit cannot yet be submitted. */
  onLocationBlockedChange?: (blocked: boolean) => void;
}

export function VisitaForm({
  onSubmit,
  loading,
  form: externalForm,
  hideSubmit = false,
  initialValues,
  preselectedCliente,
  onLocationBlockedChange,
}: VisitaFormProps) {
  const t = useT();
  const hasCobradorPermission = useHasPermission(Permission.COBRADOR_READ);
  const [internalForm] = Form.useForm();
  const form = externalForm ?? internalForm;
  const [geoEstado, setGeoEstado] = useState<GeoEstado>('inactivo');
  const [ubicacion, setUbicacion] = useState<Ubicacion | null>(null);
  const locationBlocked = hasCobradorPermission && geoEstado !== 'capturada';

  useEffect(() => {
    if (initialValues) {
      form.setFieldsValue(initialValues);
    }
  }, [initialValues, form]);

  useEffect(() => {
    if (hasCobradorPermission) solicitarUbicacion(setGeoEstado, setUbicacion);
  }, [hasCobradorPermission]);

  useEffect(() => {
    onLocationBlockedChange?.(locationBlocked);
  }, [locationBlocked, onLocationBlockedChange]);

  const handleFinish = (values: Record<string, unknown>) => {
    if (locationBlocked) return;
    const payload: Record<string, unknown> = { ...values };
    if (payload.fecha && dayjs.isDayjs(payload.fecha)) {
      payload.fecha = payload.fecha.format('YYYY-MM-DD');
    }
    if (hasCobradorPermission && ubicacion) {
      payload.latitud = ubicacion.lat;
      payload.longitud = ubicacion.lng;
    } else {
      delete payload.latitud;
      delete payload.longitud;
    }
    onSubmit(payload);
  };

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={handleFinish}
      style={{ maxWidth: 600, width: '100%' }}
    >
      <Row gutter={16}>
        <Col span={24}>
          <Form.Item
            style={itemStyle}
            name="idCliente"
            label={t('pages.visitas.cliente')}
            rules={[{ required: true, message: t('pages.visitas.clienteRequired') }]}
          >
            {hasCobradorPermission ? (
              <Select
                id="idCliente"
                aria-label={t('pages.visitas.cliente')}
                disabled
                options={
                  preselectedCliente
                    ? [
                        {
                          value: preselectedCliente.id,
                          label: `${preselectedCliente.nombre ?? `#${preselectedCliente.id}`}${
                            preselectedCliente.ip ? ` (${preselectedCliente.ip})` : ''
                          }`,
                        },
                      ]
                    : []
                }
              />
            ) : (
              <ClienteSelect />
            )}
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            style={itemStyle}
            name="resultado"
            label={t('pages.visitas.resultado')}
            rules={[{ required: true, message: t('pages.visitas.resultadoRequired') }]}
          >
            <Select
              placeholder={t('pages.visitas.selectResultado')}
              options={RESULTADO_VISITA.map((r) => ({
                value: r,
                label: t(`pages.visitas.resultados.${r}`),
              }))}
            />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            style={itemStyle}
            name="fecha"
            label={t('pages.visitas.fecha')}
            initialValue={dayjs()}
          >
            <DatePicker style={{ width: '100%' }} format={DATE_FORMAT} />
          </Form.Item>
        </Col>
        {hasCobradorPermission && (
          <Col span={24}>
            {geoEstado === 'solicitando' && (
              <Alert
                type="info"
                showIcon
                icon={<LoadingOutlined spin />}
                title={t('pages.visitas.geoSolicitando')}
              />
            )}
            {geoEstado === 'capturada' && ubicacion && (
              <Alert
                type="success"
                showIcon
                icon={<CheckCircleOutlined />}
                title={t('pages.visitas.geoCapturada', {
                  lat: ubicacion.lat.toFixed(6),
                  lng: ubicacion.lng.toFixed(6),
                })}
              />
            )}
            {(geoEstado === 'denegada' || geoEstado === 'noSoportada') && (
              <Alert
                type="error"
                showIcon
                title={
                  geoEstado === 'noSoportada'
                    ? t('pages.visitas.geoNoSoportada')
                    : t('pages.visitas.geoPermisoDenegado')
                }
                description={t('pages.visitas.geoRequerida')}
                action={
                  <Button
                    size="small"
                    onClick={() => solicitarUbicacion(setGeoEstado, setUbicacion)}
                  >
                    {t('pages.visitas.geoReintentar')}
                  </Button>
                }
              />
            )}
          </Col>
        )}
        <Col span={24}>
          <Form.Item style={itemStyle} name="comentario" label={t('pages.visitas.comentario')}>
            <Input.TextArea rows={3} placeholder={t('pages.visitas.comentarioPlaceholder')} />
          </Form.Item>
        </Col>
        <Col span={24}>
          <Form.Item style={itemStyle}>
            {!hideSubmit && (
              <Space wrap size={[8, 8]}>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={loading}
                  disabled={locationBlocked}
                >
                  {t('pages.visitas.create')}
                </Button>
              </Space>
            )}
          </Form.Item>
        </Col>
      </Row>
    </Form>
  );
}
